import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Section, FieldGrid, Button, Pill } from '../../components/EntityList';
import { useLabels } from '../../hooks/useLabels';
import { isAccountChainEnabled, isAttributionEnabled } from '../../utils/featureFlags';
import AccountService from '../../services/account.service';
import AttributionService from '../../services/attribution.service';
import { canManageAttribution } from '../../components/Attribution/attributionAccess';
import { toast } from '../../toasts';
import { getError } from '../../utils';

// Own legal entity flag (SHOW_ATTRIBUTION): entities group contracts,
// credits and bills in the attribution report. Admins toggle it in place.
function OwnEntityField({ account }) {
	const [isOwn, setIsOwn] = useState(Boolean(account.isOwnEntity));
	const [saving, setSaving] = useState(false);
	const canEdit = canManageAttribution();

	useEffect(() => {
		setIsOwn(Boolean(account.isOwnEntity));
	}, [account.isOwnEntity]);

	const toggle = async () => {
		setSaving(true);
		try {
			const { data } = await AttributionService.setOwnEntity(String(account._id), !isOwn);
			setIsOwn(Boolean(data.account?.isOwnEntity));
			toast.success(data.message || 'Cuenta actualizada');
		} catch (err) {
			toast.error(getError(err));
		} finally {
			setSaving(false);
		}
	};

	return (
		<Section
			title='Atribución'
			description='Las entidades propias agrupan contratos, pasividades y gastos en el reporte por entidad y centro.'
		>
			<div className='tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3'>
				<div className='tw:flex tw:items-center tw:gap-3'>
					<span className='tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-[0.08em] tw:text-ink-3'>Entidad propia</span>
					<Pill tone={isOwn ? 'ok' : 'neutral'} dot>{isOwn ? 'Sí' : 'No'}</Pill>
				</div>
				{canEdit && (
					<Button size='sm' onClick={toggle} disabled={saving}>
						{isOwn ? 'Quitar como entidad propia' : 'Marcar como entidad propia'}
					</Button>
				)}
			</div>
		</Section>
	);
}

// One business behind several legal entities (SHOW_ACCOUNT_CHAIN): the
// account this one continues, the one that replaced it, and the current entity.
function AccountChain({ accountId }) {
	const [chain, setChain] = useState(null);
	useEffect(() => {
		let active = true;
		AccountService.getChain(accountId)
			.then(({ data }) => { if (active) setChain(data); })
			.catch(() => { if (active) setChain(null); });
		return () => { active = false; };
	}, [accountId]);
	if (!chain || chain.chain.length < 2) return null;
	const accountLink = (a) => <Link to={`/AdminScreen/accounts/account/${a.id}`}>{a.name}</Link>;
	const fields = [
		{ label: 'Continúa a', value: chain.continuesFrom ? accountLink(chain.continuesFrom) : '—' },
		{ label: 'Continuada por', value: chain.continuedBy ? accountLink(chain.continuedBy) : '—' },
		{ label: 'Entidad vigente', value: chain.head.id === accountId ? 'Esta cuenta' : accountLink(chain.head) },
	];
	return (
		<Section title='Continuidad'>
			<FieldGrid left={fields} right={[{ label: 'Cadena', value: chain.chain.map((a) => a.name).join(' → ') }]} />
		</Section>
	);
}

function AccountInfo({ account }) {
	const { t } = useLabels();

	if (!account) {
		return null;
	}

	const isEmpresa = account.type === 'Empresa';

	const left = [
		{ label: t('field.type'), value: account.type, required: true },
		{ label: t('field.name'), value: account.name, required: true },
		{ label: t('field.email'), value: account.email, copyable: true },
		{ label: t('field.phone'), value: account.phone, copyable: true },
		{ label: t('field.folder'), value: account.folder },
		{ label: t('field.observations'), value: account.observations, area: true },
	];

	const right = [
		{ label: 'Cuenta', value: account.internalId, mono: true },
		...(isEmpresa ? [{ label: t('field.rut'), value: account.rut, mono: true, copyable: true, required: true }] : []),
		{ label: t('field.address'), value: account.address },
		{ label: t('field.currency'), value: account.currency, mono: true },
		...(isEmpresa
			? [
				{
					label: t('field.bankAccountUSD'),
					value: account.bankAccountUSD
						? `${account.bankAccountUSD.bank} - ${account.bankAccountUSD.accountNumber}`
						: null,
				},
				{
					label: t('field.bankAccountUYU'),
					value: account.bankAccountUYU
						? `${account.bankAccountUYU.bank} - ${account.bankAccountUYU.accountNumber}`
						: null,
				},
			]
			: []),
	];

	return (
		<>
			<Section title={t('title.details')}>
				<FieldGrid left={left} right={right} />
			</Section>
			{isAccountChainEnabled() && <AccountChain accountId={String(account._id)} />}
			{isAttributionEnabled() && <OwnEntityField account={account} />}
		</>
	);
}

export default AccountInfo;
